<template>
    <!-- the programme of one team: a row per match, in matchday order, the result from the team's side -->
    <div class="team-schedule">
        <div v-if="!rows.length" class="cl-empty">
            Δεν υπάρχει διαθέσιμη πληροφορία αυτή τη στιγμή.
        </div>
        <template v-else>
            <div class="team-schedule__summary">
                <div class="team-schedule__kpi">
                    <span class="team-schedule__kpi-value">{{ record.won }}</span>
                    <span class="team-schedule__kpi-label">Νίκες</span>
                </div>
                <div class="team-schedule__kpi">
                    <span class="team-schedule__kpi-value">{{ record.lost }}</span>
                    <span class="team-schedule__kpi-label">Ήττες</span>
                </div>
                <div class="team-schedule__kpi">
                    <span class="team-schedule__kpi-value">{{ record.left }}</span>
                    <span class="team-schedule__kpi-label">Επόμενοι</span>
                </div>
                <div v-if="record.form.length" class="team-schedule__form">
                    <span class="team-schedule__kpi-label">Φόρμα</span>
                    <span class="team-schedule__form-badges">
                        <span v-for="(result, index) in record.form" :key="index" class="team-schedule__badge" :class="`team-schedule__badge--${result}`">
                            {{ result === 'win' ? 'Ν' : 'Η' }}
                        </span>
                    </span>
                </div>
            </div>

            <ol class="team-schedule__list">
                <li
                    v-for="row in rows"
                    :key="row.match.id"
                    class="team-schedule__row"
                    :class="{
                        'team-schedule__row--live': row.live,
                        'team-schedule__row--next': row.next,
                        'team-schedule__row--postponed': row.match.postponed
                    }"
                    @click="openMatch(row.match)"
                >
                    <span class="team-schedule__when">
                        <span class="team-schedule__round">{{ row.match.round ? `MD ${row.match.round}` : phaseLabel(row.match) }}</span>
                        <span class="team-schedule__date">{{ formatDate(row.match.matchDatetime) }}</span>
                    </span>

                    <span class="team-schedule__opponent" @click.stop="goToTeam(row.opponent)">
                        <span class="team-schedule__vs">vs</span>
                        <span class="cl-logo team-schedule__logo">
                            <img v-if="row.opponent.logo" :src="`${assetsUrl}/${row.opponent.logo}`" :alt="row.opponent.name">
                            <v-icon v-else small color="teal">mdi-basketball-hoop</v-icon>
                        </span>
                        <span class="team-schedule__names">
                            <span class="team-schedule__name">{{ row.opponent.name }}</span>
                            <span v-if="row.match.arena" class="team-schedule__arena">
                                <v-icon x-small>mdi-map-marker-outline</v-icon>{{ row.match.arena.name }}
                            </span>
                        </span>
                    </span>

                    <span class="team-schedule__outcome">
                        <template v-if="row.live">
                            <span class="team-schedule__live"><span class="team-schedule__live-dot" />LIVE</span>
                            <span class="team-schedule__score">{{ row.own }} - {{ row.other }}</span>
                        </template>
                        <span v-else-if="row.match.postponed" class="team-schedule__postponed">Αναβολή</span>
                        <template v-else-if="row.result">
                            <span class="team-schedule__badge" :class="`team-schedule__badge--${row.result}`">{{ row.result === 'win' ? 'Ν' : 'Η' }}</span>
                            <span class="team-schedule__score">{{ row.own }} - {{ row.other }}</span>
                        </template>
                        <span v-else class="team-schedule__time">{{ formatTime(row.match.matchDatetime) }}</span>
                    </span>

                    <span class="team-schedule__actions">
                        <button v-if="row.match.videoUrl && !row.match.pending" type="button" class="team-schedule__video" title="YouTube" @click.stop="openVideo(row.match)">
                            <v-icon small color="red">mdi-youtube</v-icon>
                        </button>
                        <v-icon small class="team-schedule__go">mdi-chevron-right</v-icon>
                    </span>
                </li>
            </ol>
        </template>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { isMatchLive, refreshLiveStatuses, withLiveData, pollWhileVisible, LIVE_TICK_MS } from '@/utils/matchLive'

    const PHASES = { regular_season: 'Regular Season', playoffs: 'Play Offs', placements: 'Προκριματικά' }

    function time(match) {
        return match.matchDatetime ? momentTimezone(match.matchDatetime).valueOf() : 0
    }

    export default {
        props: {
            matches: {
                type: Array,
                default: () => []
            },
            teamId: {
                type: [Number, String],
                default: null
            }
        },
        data() {
            return {
                assetsUrl: 'https://storage.googleapis.com',
                now: Date.now(),
                stopTick: null
            }
        },
        computed: {
            // every match from the team's side: the opponent, the two scores and the result
            rows() {
                const rows = [...(this.matches || [])]
                    .sort((a, b) => time(a) - time(b))
                    .map(original => {
                        const match = withLiveData(original, this.now)
                        const home = String(match.team1 && match.team1.id) === String(this.teamId)
                        const own = home ? match.scoreTeam1 : match.scoreTeam2
                        const other = home ? match.scoreTeam2 : match.scoreTeam1
                        const live = isMatchLive(match, this.now)
                        const played = !live && !match.pending && !match.postponed && !(own === 0 && other === 0)
                        return {
                            match,
                            opponent: (home ? match.team2 : match.team1) || {},
                            own,
                            other,
                            live,
                            result: played ? (own > other ? 'win' : 'loss') : null
                        }
                    })
                // the next match to be played gets a mark
                const next = rows.find(row => !row.result && !row.live && !row.match.postponed)
                if (next) next.next = true
                return rows
            },
            record() {
                const played = this.rows.filter(row => row.result)
                return {
                    won: played.filter(row => row.result === 'win').length,
                    lost: played.filter(row => row.result === 'loss').length,
                    left: this.rows.filter(row => !row.result && !row.match.postponed).length,
                    form: played.slice(-5).map(row => row.result)
                }
            }
        },
        watch: {
            matches: {
                handler() {
                    refreshLiveStatuses(this.matches || [])
                },
                immediate: true
            }
        },
        created() {
            this.stopTick = pollWhileVisible(() => {
                this.now = Date.now()
                refreshLiveStatuses(this.matches || [], this.now)
            }, LIVE_TICK_MS)
        },
        beforeDestroy() {
            this.stopTick()
        },
        methods: {
            phaseLabel(match) {
                return match.cup ? 'Κύπελλο' : (PHASES[match.phase] || '')
            },
            formatDate(date) {
                return date ? momentTimezone(date).tz('Europe/Athens').locale('el').format('ddd D MMM') : '–'
            },
            formatTime(date) {
                return date ? momentTimezone(date).tz('Europe/Athens').format('HH:mm') : '–:–'
            },
            openMatch(match) {
                this.$router.push({ name: 'Match', params: { id: match.id } })
            },
            goToTeam(team) {
                if (team && team.id) this.$router.push({ name: 'Team', params: { id: team.id } })
            },
            openVideo(match) {
                window.open(match.videoUrl.replace('embed/', 'watch?v='), '_blank', 'noopener')
            }
        }
    }
</script>

<style scoped>
.team-schedule {
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
}

/* ---------- summary ---------- */
.team-schedule__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
}

.team-schedule__kpi {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 84px;
    padding: 8px 14px;
    border-radius: 10px;
    background: #232323;
    color: #fff;
}

.team-schedule__kpi-value {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    line-height: 1;
}

.team-schedule__kpi-label {
    margin-top: 3px;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
}

.team-schedule__form {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 10px;
    background: #232323;
}

.team-schedule__form-badges {
    display: flex;
    gap: 4px;
}

/* ---------- the list ---------- */
.team-schedule__list {
    margin: 0;
    padding: 0;
    list-style: none;
    border-radius: 12px;
    background: #232323;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.12);
    overflow: hidden;
}

.team-schedule__row {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;
}

.team-schedule__row + .team-schedule__row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.team-schedule__row:hover {
    background: rgba(3, 229, 183, 0.06);
}

.team-schedule__row--next {
    background: linear-gradient(90deg, rgba(3, 229, 183, 0.14) 0%, rgba(3, 122, 222, 0.04) 100%);
    box-shadow: inset 3px 0 0 #03e5b7;
}

.team-schedule__row--live {
    background: rgba(239, 68, 68, 0.1);
    box-shadow: inset 3px 0 0 #ef4444;
}

.team-schedule__row--postponed {
    opacity: 0.7;
}

.team-schedule__when {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.team-schedule__round {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.15rem;
    line-height: 1;
    color: #03e5b7;
}

.team-schedule__date {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    text-transform: capitalize;
}

.team-schedule__opponent {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.team-schedule__vs {
    font-size: 0.72rem;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.4);
}

.team-schedule__logo {
    width: 36px;
    height: 36px;
}

.team-schedule__names {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.team-schedule__name {
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.team-schedule__opponent:hover .team-schedule__name {
    color: #03e5b7;
}

.team-schedule__arena {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.5);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.team-schedule__arena .v-icon {
    color: inherit !important;
}

.team-schedule__outcome {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 110px;
}

.team-schedule__score {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.5rem;
    line-height: 1;
    white-space: nowrap;
}

.team-schedule__time {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.5rem;
    line-height: 1;
    color: #03e5b7;
}

.team-schedule__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 900;
    color: #fff;
}

.team-schedule__badge--win {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.team-schedule__badge--loss {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.75);
}

.team-schedule__live,
.team-schedule__postponed {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

.team-schedule__live {
    background: #ef4444;
}

.team-schedule__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: team-schedule-live 1.2s ease-in-out infinite;
}

@keyframes team-schedule-live {
    50% {
        opacity: 0.25;
    }
}

.team-schedule__postponed {
    background: #f44336;
}

.team-schedule__actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.team-schedule__video {
    display: inline-flex;
    padding: 4px;
    border-radius: 6px;
}

.team-schedule__video:hover {
    background: rgba(255, 255, 255, 0.08);
}

.team-schedule__go {
    color: rgba(255, 255, 255, 0.35) !important;
}

.team-schedule__row:hover .team-schedule__go {
    color: #03e5b7 !important;
}

/* phones: the date above, the opponent and the result on one line */
@media (max-width: 700px) {
    .team-schedule__row {
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-areas:
            'when when'
            'opponent outcome';
        row-gap: 8px;
        padding: 10px 12px;
    }

    .team-schedule__when {
        grid-area: when;
        flex-direction: row;
        align-items: baseline;
        gap: 8px;
    }

    .team-schedule__opponent {
        grid-area: opponent;
    }

    .team-schedule__outcome {
        grid-area: outcome;
        min-width: 0;
    }

    .team-schedule__actions {
        display: none;
    }

    .team-schedule__kpi {
        min-width: 70px;
        padding: 6px 10px;
    }
}
</style>
